/**
 * Validators custom pour les conditions liées au graphe energyuse
 */

import { EnergyUse } from "@modules/energy-manager/helpers/energyuse"
import type { EnergyUseForm } from "@modules/energy-manager/helpers/energyuse"
import type {
    CustomValidator,
    ContextBuilder,
    ValidatorWithContext,
} from "./registry"
import type { Building } from "@types"

/**
 * Construit le contexte enrichi pour le validator energyuse_has_input
 * @param data - Le bâtiment ou les sources
 * @param condition - La condition à évaluer
 * @returns Le contexte avec le graphe energyuse construit
 */
const buildEnergyuseContext: ContextBuilder = (data: Building | any[]): any => {
    const context: any = {}

    // Si c'est un building, construire le graphe energyuse
    if (data && typeof data === "object") {
        const building = data as Building
        context.building = building

        // Déterminer le niveau et le type d'énergie utilisé
        const niveau =
            building?.infos && "niveau" in building.infos
                ? building.infos.niveau
                : "immeuble"

        const field = ["appartement", "maison_appartement", "maison"].includes(
            niveau! as string
        )
            ? "logement"
            : "batiment"

        // Récupérer les données d'énergie utilisées
        const energyuseData = building?.infos?.[field]?.energyuse

        const energyUseGraph = new EnergyUse(energyuseData)

        const energyUseForm = energyUseGraph.createFormFromGraph()

        return {
            energyuseForm: energyUseForm,
        }
    }

    return context
}

/**
 * Validator pour vérifier si un noeud du graphe energyuse a une valeur spécifique dans son tableau idname_inputs
 *
 * @param value - Tableau de valeurs à chercher dans idname_inputs (ex: ["gaz"])
 * @param context - Contexte contenant le building et le graphe energyuse construit
 * @returns true si un noeud du graphe a au moins une des valeurs dans idname_inputs
 *
 * @example
 * Condition:
 * {
 *   "operator": "custom",
 *   "validatorId": "energyuse_chauffage_has_input",
 *   "origin": "building"
 * }
 *
 * Ce validator vérifie si dans le graphe energyuse, il existe un noeud
 * (par exemple "chaudiere_gaz") qui a "gaz" dans son tableau idname_inputs.
 */
const energyuseChauffageHasInputValidator: CustomValidator = (
    _: any,
    context: { energyuseForm: EnergyUseForm }
): boolean => {
    const isChauffageGaz = context.energyuseForm?.chauffage === "gaz"
    const isEcsNotGaz = context.energyuseForm?.ecs !== "gaz"

    return isChauffageGaz && isEcsNotGaz
}

/**
 * Validator energyuse_chauffage_has_input avec son buildContext
 */
export const energyuseChauffageHasInput: ValidatorWithContext = {
    validator: energyuseChauffageHasInputValidator,
    buildContext: buildEnergyuseContext,
}
